import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import './Header.css';

// Try to import logo from icons folder, but provide fallback if it doesn't exist
let logo;
try {
  logo = require('../../assets/icons/logo.png');
} catch (error) {
  logo = null;
}

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: #7F1B1C;
  z-index: 1000;
  padding: 0;
  border-top: 2px solid #640003;
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 10px;
  height: 80px;

  @media (max-width: 768px) {
    padding: 0 10px;
    height: 70px;
  }
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: 100%;
  justify-content: stretch;
  margin-left: 5px;

  @media (max-width: 768px) {
    margin-left: 5px;
  }
`;

const LogoImage = styled.img`
  height: 100%;
  width: auto;
  object-fit: contain;
  margin-left: 5px;

  @media (max-width: 768px) {
    margin-left: 5px;
  }
`;

const FallbackLogo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  height: 100%;
  justify-content: stretch;
`;

const FallbackText = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: white;
  font-family: 'Arial', sans-serif;
  letter-spacing: 1px;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const FallbackSubtext = styled.div`
  font-size: 0.6rem;
  color: #ccc;
  font-weight: 300;
  margin-top: -0.1rem;

  @media (max-width: 768px) {
    font-size: 0.5rem;
  }
`;

const CenterMenu = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 60px;

  @media (max-width: 768px) {
    margin-left: 0;
    flex: 1;
    justify-content: flex-end;
  }
`;

const DesktopNav = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const DesktopNavLink = styled(Link)`
  color: #ccc;
  text-decoration: none;
  font-size: 0.75rem;
  font-weight: 500;
  transition: color 0.3s ease;
  padding: 0.5rem 0.5rem;
  border-radius: 4px;
  white-space: nowrap;

  &:hover {
    color: white;
  }

  &.active {
    color: white;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const NavDropdownPanel = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 11rem;
  background: #640003;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: 6px 0;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-6px);
  transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s;
  pointer-events: none;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.35);
  z-index: 1100;
`;

const NavDropdownRoot = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;

  &:hover ${NavDropdownPanel},
  &:focus-within ${NavDropdownPanel} {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);
    pointer-events: auto;
  }
`;

const PayParkingTrigger = styled(DesktopNavLink)`
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
`;

const NavDropdownItem = styled(Link)`
  display: block;
  padding: 0.55rem 1rem;
  color: #e2e8f0;
  font-size: 0.75rem;
  font-weight: 500;
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const MenuButton = styled.button`
  width: 40px;
  height: 40px;
  background: #2F4F4F;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #1a2f2f;
  }

  @media (min-width: 769px) {
    display: none;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const PhoneIcon = styled.div`
  width: 24px;
  height: 24px;
  background: #8B0000;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.75rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const PhoneNumber = styled.div`
  color: white;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 0.7rem;
    display: none;
  }
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(139, 0, 0, 0.98);
  z-index: 1001;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  padding-top: 80px;
  gap: 1.5rem;
  transform: ${props => props.isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s ease;
`;

const MobileMenuHeader = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background: rgba(139, 0, 0, 0.95);
`;

const MobileMenuTitle = styled.div`
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
`;

const MobileMenuLink = styled(Link)`
  color: white;
  font-size: 1.1rem;
  font-weight: 500;
  text-decoration: none;
  transition: color 0.3s ease;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  width: 80%;
  text-align: center;

  &:hover {
    color: #FFD700;
    background: rgba(255, 255, 255, 0.1);
  }
`;

const MobileMenuSubLink = styled(Link)`
  color: rgba(255, 255, 255, 0.92);
  font-size: 1rem;
  font-weight: 500;
  text-decoration: none;
  transition: color 0.3s ease, background 0.3s ease;
  padding: 0.55rem 2rem 0.55rem 3rem;
  border-radius: 8px;
  width: 80%;
  text-align: left;

  &:hover {
    color: #ffd700;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const MobilePhoneNumber = styled.div`
  color: white;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  width: 80%;
  background: rgba(255, 255, 255, 0.1);
  margin-top: 0.5rem;
`;

const MobilePhoneIcon = styled.div`
  width: 24px;
  height: 24px;
  background: #8B0000;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 0.75rem;
`;

const CloseMenuButton = styled.button`
  width: 40px;
  height: 40px;
  background: #2F4F4F;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #1a2f2f;
  }
`;

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    // Prevent body scroll when mobile menu is open
    if (!isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    document.body.style.overflow = 'unset';
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname === path;
  };

  const payParkingActive =
    location.pathname === '/smart-parking'
    || location.pathname.startsWith('/smart-parking/open-tickets')
    || location.pathname.startsWith('/smart-parking/pay');

  // Clean up body overflow when component unmounts
  useEffect(() => {
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <HeaderContainer>
      <Nav>
        <LogoContainer>
          {logo ? (
            <LogoImage src={logo} alt="Keyplot Investments Logo" />
          ) : (
            <FallbackLogo>
              <FallbackText>KPI</FallbackText>
              <FallbackSubtext>Keyplot Investments Logo</FallbackSubtext>
            </FallbackLogo>
          )}
        </LogoContainer>
        
        <CenterMenu>
          <DesktopNav>
            <DesktopNavLink to="/" className={isActive('/') ? 'active' : ''}>Home</DesktopNavLink>
            <DesktopNavLink to="/about" className={isActive('/about') ? 'active' : ''}>About Us</DesktopNavLink>
            <DesktopNavLink to="/parking-options" className={isActive('/parking-options') ? 'active' : ''}>Parking Rates</DesktopNavLink>
            <DesktopNavLink to="/kp-card" className={isActive('/kp-card') ? 'active' : ''}>KP Card</DesktopNavLink>
            <NavDropdownRoot>
              <PayParkingTrigger to="/smart-parking" className={payParkingActive ? 'active' : ''}>
                Pay Parking
                <ChevronDown size={14} aria-hidden />
              </PayParkingTrigger>
              <NavDropdownPanel>
                <NavDropdownItem to="/smart-parking/open-tickets">Open Tickets</NavDropdownItem>
              </NavDropdownPanel>
            </NavDropdownRoot>
            <DesktopNavLink to="/careers" className={isActive('/careers') ? 'active' : ''}>Careers</DesktopNavLink>
            <DesktopNavLink to="/contact" className={isActive('/contact') ? 'active' : ''}>Contact Us</DesktopNavLink>
          </DesktopNav>
          <MenuButton onClick={toggleMobileMenu}>
            <Menu size={20} />
          </MenuButton>
        </CenterMenu>

        <ContactInfo>
          <PhoneIcon>
            <Phone size={12} />
          </PhoneIcon>
          <PhoneNumber>+264 857 208 426 / +264 61 277 824</PhoneNumber>
        </ContactInfo>
      </Nav>

              <MobileMenu isOpen={isMobileMenuOpen}>
          <MobileMenuHeader>
            <MobileMenuTitle>Menu</MobileMenuTitle>
            <CloseMenuButton onClick={closeMobileMenu}>
              <X size={20} />
            </CloseMenuButton>
          </MobileMenuHeader>
          <MobileMenuLink to="/" onClick={closeMobileMenu}>Home</MobileMenuLink>
          <MobileMenuLink to="/about" onClick={closeMobileMenu}>About Us</MobileMenuLink>
          <MobileMenuLink to="/parking-options" onClick={closeMobileMenu}>Parking Rates</MobileMenuLink>
          <MobileMenuLink to="/kp-card" onClick={closeMobileMenu}>KP Card</MobileMenuLink>
          <MobileMenuLink to="/smart-parking" onClick={closeMobileMenu}>Pay Parking</MobileMenuLink>
          <MobileMenuSubLink to="/smart-parking/open-tickets" onClick={closeMobileMenu}>
            Open Tickets
          </MobileMenuSubLink>
          <MobileMenuLink to="/careers" onClick={closeMobileMenu}>Careers</MobileMenuLink>
          <MobileMenuLink to="/contact" onClick={closeMobileMenu}>Contact Us</MobileMenuLink>
          <MobilePhoneNumber>
            <MobilePhoneIcon>
              <Phone size={12} />
            </MobilePhoneIcon>
            +264 857 208 426 / +264 61 277 824
          </MobilePhoneNumber>
        </MobileMenu>
    </HeaderContainer>
  );
};

export default Header; 
